import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, Tabs, Tab, Box, Typography, Paper, Divider, List, ListItem, ListItemText, ListItemSecondaryAction, ListSubheader,
} from '@material-ui/core';
import { Entity } from '@backstage/catalog-model';
import { CopyTextButton } from '@backstage/core-components';

type TabId = 'overview' | 'json' | 'yaml';

const Section: React.FC<{ title: string }> = ({ title, children }) => (
  <Paper elevation={2} style={{ padding: 20, marginBottom: 24 }}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    <Divider style={{ marginBottom: 12 }} />
    <Box>{children}</Box>
  </Paper>
);

const OverviewTab: React.FC<{ entity: Entity }> = ({ entity }) => {
  // Extract relevant fields
  const { apiVersion, kind, spec, metadata, status, relations = [] } = entity;
  const entityRef = `${kind?.toLowerCase()}:${metadata?.namespace || 'default'}/${metadata?.name}`;

  return (
    <Box p={1}>
      <Typography variant="h5" gutterBottom>Entity Overview</Typography>
      <Section title="Identity">
        <List dense>
          <ListItem>
            <ListItemText primary="apiVersion" secondary={apiVersion} />
          </ListItem>
          <ListItem>
            <ListItemText primary="kind" secondary={kind} />
          </ListItem>
          <ListItem>
            <ListItemText primary="entityRef" secondary={entityRef} />
            <ListItemSecondaryAction>
              <CopyTextButton text={entityRef} />
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </Section>
      <Section title="Metadata">
        {!!Object.keys(metadata?.annotations || {}).length && (
          <List dense subheader={<ListSubheader disableSticky>Annotations</ListSubheader>}>
            {Object.entries(metadata?.annotations ?? {}).map(entry => (
              <ListItem key={entry[0]}>
                <ListItemText primary={entry[0]} secondary={entry[1]} />
              </ListItem>
            ))}
          </List>
        )}
        {!!Object.keys(metadata?.labels || {}).length && (
          <List dense subheader={<ListSubheader disableSticky>Labels</ListSubheader>}>
            {Object.entries(metadata?.labels ?? {}).map(entry => (
              <ListItem key={entry[0]}>
                <ListItemText primary={entry[0]} secondary={entry[1]} />
              </ListItem>
            ))}
          </List>
        )}
        {!!metadata?.tags?.length && (
          <List dense subheader={<ListSubheader disableSticky>Tags</ListSubheader>}>
            {metadata.tags.map((tag, index) => (
              <ListItem key={`${tag}-${index}`}>
                <ListItemText primary={tag} />
              </ListItem>
            ))}
          </List>
        )}
      </Section>
      {!!relations.length && (
        <Section title="Relations">
          <List dense>
            {relations.map((rel, idx) => (
              <ListItem key={idx}>
                <ListItemText primary={rel.type} secondary={(() => {
                  if (rel.target && rel.target.name) return rel.target.name;
                  if (rel.targetRef) {
                    const ref = rel.targetRef;
                    return ref.includes('/') ? ref.split('/').pop() : ref;
                  }
                  return '';
                })()} />
              </ListItem>
            ))}
          </List>
        </Section>
      )}

      {!!status?.items?.length && (
        <Section title="Status">
          {status.items.map((item, index) => (
            <Box key={index} mb={1}>
              <Typography variant="body2" color="textSecondary">
                {item.level}: {item.type}
              </Typography>
              <Box ml={2}>{item.message}</Box>
            </Box>
          ))}
        </Section>
      )}
    </Box>
  );
};


// const YamlTab: React.FC<{ entity: Entity }> = ({ entity }) => {
//   // Render YAML with styled key and value
//   const renderYamlStyled = (obj: any, indent = 0): React.ReactNode[] => {
//     if (obj == null) return [];
//     const pad = '  '.repeat(indent);
//     const keyStyle = { color: '#d3d3d3' }; // greyish white
//     const valueStyle = { color: '#d66262' }; // lightish red
//     let lines: React.ReactNode[] = [];
//     if (Array.isArray(obj)) {
//       if (obj.length === 0) return [<span key={pad + 'empty'} style={valueStyle}>[]</span>];
//       obj.forEach((item, idx) => {
//         if (typeof item === 'object' && item !== null) {
//           lines.push(
//             <span key={pad + idx}>
//               {pad}-<br />
//               {renderYamlStyled(item, indent + 1)}
//             </span>
//           );
//         } else {
//           lines.push(
//             <span key={pad + idx}>
//               {pad}- <span style={valueStyle}>{String(item)}</span>
//               <br />
//             </span>
//           );
//         }
//       });
//       return lines;
//     }
//     if (typeof obj === 'object') {
//       Object.entries(obj).forEach(([key, value], idx) => {
//         if (typeof value === 'object' && value !== null && (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0)) {
//           lines.push(
//             <span key={pad + key + idx}>
//               <span style={keyStyle}>{pad}{key}:</span><br />
//               {renderYamlStyled(value, indent + 1)}
//             </span>
//           );
//         } else if (Array.isArray(value) && value.length === 0) {
//           lines.push(
//             <span key={pad + key + idx}>
//               <span style={keyStyle}>{pad}{key}:</span> <span style={valueStyle}>[]</span><br />
//             </span>
//           );
//         } else {
//           lines.push(
//             <span key={pad + key + idx}>
//               <span style={keyStyle}>{pad}{key}:</span> <span style={valueStyle}>{value === null || value === undefined ? '' : String(value)}</span><br />
//             </span>
//           );
//         }
//       });
//       return lines;
//     }
//     return [<span key={pad + 'val'} style={valueStyle}>{pad + String(obj)}<br /></span>];
//   };
//   return (
//     <Section title="Raw YAML">

//     <pre style={{ fontFamily: 'monospace', fontSize: 12, background: 'transparent', margin: 0, padding: 8, whiteSpace: 'pre-wrap'}}>
//       {renderYamlStyled(entity)}
//     </pre>
//     </Section>
//   );
// };

export function MyInspectEntityDialog({
  open, onClose, entity, initialTab = 'overview',
}: {
  open: boolean;
  onClose: () => void;
  entity: Entity;
  initialTab?: TabId;
}) {
  const [tab, setTab] = React.useState<TabId>(initialTab);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogTitle>Entity Inspector</DialogTitle>
      <DialogContent>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Overview" value="overview" />
          {/* <Tab label="YAML" value="yaml" /> */}
        </Tabs>
        <Box mt={2}>
          {tab === 'overview' && <OverviewTab entity={entity} />}
          {/* {tab === 'yaml' && <YamlTab entity={entity} />} */}
        </Box>
      </DialogContent>
    </Dialog>
  );
}